import { Icon } from 'components/icons/icon';
import { usePanel } from 'hooks/usePanel';
import { Sizes } from 'enums/sizes';
import { QuoteMandateForm } from '../mandate/QuoteMandateForm';
import { useEffect } from 'react';
import { LightContractCard } from '../cards/LightContractCard';
import { CardsSection } from 'components/layout/Section/CardsSection';
import { NsKey } from 'enums/NsKey';
import { TitleIconFactory } from 'factories/TitleIconFactory';
import { TitleIconKey } from 'enums/titleIconKey';
import { useDynamicTranslation } from 'hooks/useDynamicTranslation';
import { RiskInformation } from './RiskInformation';
import { PersonWithDocuments } from 'features/new-actor/components/PersonWithDocuments';
import { BranchCode } from 'api/risk';
import {
  useQuoteContractualizationData,
  useContractualizationActions,
  useConditionCardStatus,
  useInsuredCardStatus,
  useRiskCardStatus,
  useIsInfoCardsDisabled,
  useCanFetchDocuments
} from '../../store';
import { useRefetchQuoteData } from '../../hooks/useRefetchQuoteData';
import { useSubscriptionProcessActions } from '../../../contexts/SubscriptionProcessContext';
import { withPersonName } from 'shared/utils/panelTitle';

/**
 * Component that displays contract information, including risk, insureds, and tenants
 */
export const ContractInformation: React.FC = () => {
  const quoteData = useQuoteContractualizationData();
  const { fetchSimulationDocuments } = useContractualizationActions();
  const { refreshSubscriptionProcess } = useSubscriptionProcessActions();
  const refetchQuoteData = useRefetchQuoteData();

  const { openPanel } = usePanel();
  const { t } = useDynamicTranslation([NsKey.Contractualization, NsKey.Common]);

  // Utilisation des selecteurs centralises
  const conditionCardStatus = useConditionCardStatus();
  const insuredCardStatus = useInsuredCardStatus();
  const riskCardStatus = useRiskCardStatus();
  const isCardDisabled = useIsInfoCardsDisabled();
  const canFetchDocuments = useCanFetchDocuments();

  const onInsuredCardClick = (personId: string) => {
    openPanel(
      {
        title: withPersonName(
          t('COMMON.LABELS.EDIT_INSURED_PERSON'),
          quoteData?.insured.displayName
        ),
        size: Sizes['2xl']
      },
      <PersonWithDocuments
        personId={personId}
        onSuccess={refreshSubscriptionProcess}
        onDocumentChange={refetchQuoteData}
      />
    );
  };

  const onSelectConditions = () => {
    if (!quoteData) return;
    openPanel(
      {
        title: t('CONTRACTUALIZATION.TITLE.MANDATE'),
        size: Sizes['2xl']
      },
      <QuoteMandateForm onSuccess={refreshSubscriptionProcess} />
    );
  };

  useEffect(() => {
    if (canFetchDocuments && quoteData) {
      fetchSimulationDocuments(
        quoteData.quoteId,
        (quoteData.branchCode as BranchCode) || BranchCode.Unemployment
      );
    }
  }, [fetchSimulationDocuments, quoteData, canFetchDocuments]);

  return (
    <CardsSection title={t('CONTRACTUALIZATION.CARDS.CONTRACT_INFOS_TITLE')}>
      <div className="grid grid-cols-2 gap-4">
        {/* Risk infos */}
        <RiskInformation
          status={riskCardStatus}
          isCardDisabled={isCardDisabled}
        />
        {/* Contract insured */}
        <LightContractCard
          icon={
            <Icon
              name="circle-owner"
              styles="svg-primary"
              width="34"
              height="34"
            />
          }
          title={quoteData?.insured.displayName}
          status={insuredCardStatus}
          disabled={isCardDisabled}
          onClick={() =>
            quoteData &&
            quoteData.insured.id &&
            onInsuredCardClick(quoteData.insured.id)
          }
        />
        {/* Contract conditions */}
        <LightContractCard
          className="col-span-2"
          icon={<TitleIconFactory titleIconKey={TitleIconKey.User} />}
          title={'CONTRACTUALIZATION.CARDS.UNEMPLOYMENT_CONDITIONS_TITLE'}
          subtitle={'CONTRACTUALIZATION.CARDS.UNEMPLOYMENT_CONDITIONS_SUBTITLE'}
          status={conditionCardStatus}
          disabled={isCardDisabled}
          onClick={() => onSelectConditions()}
        />
      </div>
    </CardsSection>
  );
};
